import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Tooltip,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MenuBookIcon from '@mui/icons-material/MenuBook';

const SNIPPET_MAX_LENGTH = 300;

const truncateText = (text) => {
    if (!text) return '';
    if (text.length <= SNIPPET_MAX_LENGTH) return text;
    return `${text.slice(0, SNIPPET_MAX_LENGTH)}…`;
};

const formatScore = (score) =>
    typeof score === 'number' ? `${(score * 100).toFixed(1)}%` : null;

// Group retrieval passages by the document they came from. Grouping key:
// document_name (permissive about casing), falling back to document_id
// when the name is missing; passages with neither stay ungrouped (one
// group each, labeled "Source N" downstream). Pure data helper — no React.
export const groupDocumentsByName = (documents) => {
    const groups = [];
    const byKey = new Map();

    (documents || []).forEach((doc, index) => {
        const name = doc?.document_name || doc?.documentName || null;
        const key =
            name ??
            (doc?.document_id != null
                ? `__id__:${doc.document_id}`
                : `__idx__:${index}`);

        let group = byKey.get(key);
        if (!group) {
            group = { name, passages: [] };
            byKey.set(key, group);
            groups.push(group);
        }
        group.passages.push(doc);
    });

    return groups;
};

// Count of unique documents behind a passage array — single source of
// truth for the References pill and the modal.
export const getUniqueDocumentCount = (documents) =>
    groupDocumentsByName(documents).length;

// One retrieval passage: source ref (page/slide/section), relevance
// score and content snippet. Same fields as before the grouping change.
const CitationPassage = ({ doc }) => {
    const sourceRef =
        doc?.source?.source_ref ||
        doc?.source?.slide_title ||
        doc?.source?.section_title ||
        null;
    const score = formatScore(doc?.score);

    return (
        <Box sx={{ borderLeft: '2px solid', borderColor: 'divider', pl: 1.25 }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 0.25,
                }}
            >
                {sourceRef && (
                    <Typography
                        variant="caption"
                        sx={{ fontWeight: 600, color: 'text.primary' }}
                    >
                        {sourceRef}
                    </Typography>
                )}
                {score && (
                    <Typography
                        variant="caption"
                        sx={{
                            ml: 'auto',
                            fontWeight: 600,
                            color: 'success.main',
                            flexShrink: 0,
                        }}
                    >
                        {score}
                    </Typography>
                )}
            </Box>
            <Typography
                variant="caption"
                sx={{
                    display: 'block',
                    color: 'text.secondary',
                    lineHeight: 1.5,
                    overflowWrap: 'break-word',
                }}
            >
                {truncateText(doc?.text)}
            </Typography>
        </Box>
    );
};

// Collapsible card for one unique document: name header (tooltip when
// the CSS ellipsis kicks in) + passage count, passages inside.
const DocumentAccordion = ({ group, index, defaultExpanded }) => {
    const intl = useIntl();
    const nameRef = useRef(null);
    const [isTruncated, setIsTruncated] = useState(false);

    const displayName =
        group.name ||
        intl.formatMessage(
            { id: 'askPrompta.reference_fallback', defaultMessage: 'Source {number}' },
            { number: index + 1 }
        );

    useEffect(() => {
        const el = nameRef.current;
        if (el) {
            setIsTruncated(el.scrollWidth > el.clientWidth);
        }
    }, [displayName]);

    return (
        <Accordion
            disableGutters
            elevation={0}
            defaultExpanded={defaultExpanded}
            sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '12px !important',
                bgcolor: 'background.default',
                overflow: 'hidden',
                '&:before': { display: 'none' },
            }}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
                sx={{
                    minHeight: 44,
                    px: 1.5,
                    '& .MuiAccordionSummary-content': {
                        my: 0.75,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        minWidth: 0,
                    },
                }}
            >
                <Tooltip
                    title={group.name || ''}
                    placement="top"
                    arrow
                    disableHoverListener={!isTruncated}
                >
                    <Typography
                        ref={nameRef}
                        sx={{
                            fontWeight: 600,
                            fontSize: 13.5,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: 360,
                            display: 'block',
                        }}
                    >
                        {displayName}
                    </Typography>
                </Tooltip>
                <Typography
                    sx={{ fontSize: 12, color: 'text.secondary', flexShrink: 0 }}
                >
                    ({group.passages.length})
                </Typography>
            </AccordionSummary>
            <AccordionDetails
                sx={{
                    pt: 0,
                    px: 1.5,
                    pb: 1.5,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.25,
                }}
            >
                {group.passages.map((doc, passageIndex) => (
                    <CitationPassage key={passageIndex} doc={doc} />
                ))}
            </AccordionDetails>
        </Accordion>
    );
};

// Citation sources dialog for the retrieval documents grounding an
// assistant answer: one collapsible card per unique document, the
// individual passages inside. Renders nothing when there are no
// documents (e.g. survey-only answers).
const Citations = ({ documents }) => {
    const intl = useIntl();
    const [open, setOpen] = useState(false);
    const titleId = useId();

    // group by document, passages sorted by score desc inside each
    // group, groups sorted by their best passage score desc
    const documentGroups = useMemo(() => {
        const groups = groupDocumentsByName(documents).map((group) => ({
            ...group,
            passages: [...group.passages].sort(
                (a, b) => (b?.score ?? 0) - (a?.score ?? 0)
            ),
        }));
        return groups.sort(
            (a, b) =>
                (b.passages[0]?.score ?? 0) - (a.passages[0]?.score ?? 0)
        );
    }, [documents]);

    const passageCount = (documents || []).length;

    if (documentGroups.length === 0) return null;

    const buttonLabel = intl.formatMessage(
        { id: 'askPrompta.references', defaultMessage: 'References ({count})' },
        { count: documentGroups.length }
    );

    return (
        <Box sx={{ display: 'inline-flex' }}>
            <Button
                size="small"
                variant="text"
                onClick={() => setOpen(true)}
                aria-haspopup="dialog"
                startIcon={<MenuBookIcon sx={{ fontSize: 14 }} />}
                sx={{
                    textTransform: 'none',
                    color: 'text.secondary',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    p: 0.5,
                    minHeight: 'auto',
                    '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
                }}
            >
                {buttonLabel}
            </Button>

            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                maxWidth="sm"
                fullWidth
                aria-labelledby={titleId}
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle id={titleId} sx={{ pb: 0.5 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}
                    >
                        {intl.formatMessage({
                            id: 'askPrompta.references_dialog_title',
                            defaultMessage: 'Sources',
                        })}
                        <IconButton
                            size="small"
                            onClick={() => setOpen(false)}
                            aria-label={intl.formatMessage({
                                id: 'askPrompta.close',
                                defaultMessage: 'Close',
                            })}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                    <Typography
                        variant="body2"
                        sx={{ color: 'text.secondary', mt: 0.25 }}
                    >
                        {intl.formatMessage(
                            {
                                id: 'askPrompta.references_dialog_subtitle',
                                defaultMessage: '{count} passages used for this answer',
                            },
                            { count: passageCount }
                        )}
                    </Typography>
                </DialogTitle>
                <Divider sx={{ mt: 1.5 }} />
                <DialogContent dividers={false}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {documentGroups.map((group, index) => (
                            <DocumentAccordion
                                key={index}
                                group={group}
                                index={index}
                                defaultExpanded={documentGroups.length === 1}
                            />
                        ))}
                    </Box>
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default Citations;
